import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Button,
  Stack,
  Fab,
  Tooltip,
  Paper,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import axios from '../../config/axios';
import { config, getApiUrl } from '../../config/environment';
import {
  JoinPayload,
  joinAsProfessional,
  leaveSessionAuth,
} from '../../services/teleconsultService';
import TeleconsultaRoom from './TeleconsultaRoom';
import HealthcareEvolutionDialog from '../../components/HealthcareEvolutionDialog';
import TeleconsultCallTimer from './TeleconsultCallTimer';
import TeleconsultAgendaDrawer from './TeleconsultAgendaDrawer';
import { teleconsultPageColumnSx, teleconsultPageSx, teleconsultPanelCardSx } from './teleconsultLayout';

function mapAppointmentToPatient(apt: any): { id: string; full_name: string } | null {
  const pid = apt?.patient_id;
  if (!pid) return null;
  const cp = apt?.clinic_patients;
  const name =
    (typeof cp?.social_name === 'string' && cp.social_name.trim()) ||
    (typeof cp?.full_name === 'string' && cp.full_name.trim()) ||
    (typeof apt?.patient_name_snapshot === 'string' && apt.patient_name_snapshot.trim()) ||
    'Paciente';
  return { id: pid, full_name: name };
}

/**
 * Rota autenticada /clinic/teleconsulta/:appointmentId
 *
 * Sala Jitsi à esquerda e o mesmo fluxo «Nova evolução clínica» à direita (desktop).
 * Em telas estreitas, empilha vídeo e formulário com rolagem na página.
 */
const ProfessionalTeleconsultPage: React.FC = () => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [join, setJoin] = useState<JoinPayload | null>(null);

  const [evolutionOpen, setEvolutionOpen] = useState(true);
  const [agendaDrawerOpen, setAgendaDrawerOpen] = useState(false);
  const [callStartedAt, setCallStartedAt] = useState<number | null>(null);
  const [resolvedPatient, setResolvedPatient] = useState<{ id: string; full_name: string } | null>(
    null,
  );
  const [patientFetchError, setPatientFetchError] = useState('');
  const [patientLoading, setPatientLoading] = useState(false);

  const init = useCallback(async () => {
    if (!appointmentId) return;
    setLoading(true);
    setError('');
    try {
      const data = await joinAsProfessional(appointmentId);
      setJoin(data);
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.message ||
        'Não foi possível entrar na sala.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    if (!join?.appointmentId) return;

    if (join.patient?.id) {
      setResolvedPatient(null);
      setPatientFetchError('');
      setPatientLoading(false);
      return;
    }

    let cancelled = false;
    setPatientLoading(true);
    setPatientFetchError('');

    (async () => {
      try {
        const { data } = await axios.get(getApiUrl(`/clinic/appointments/${join.appointmentId}`));
        const mapped = mapAppointmentToPatient(data?.data);
        if (cancelled) return;
        if (mapped) setResolvedPatient(mapped);
        else setPatientFetchError('Agendamento sem paciente vinculado.');
      } catch (e: any) {
        if (!cancelled) {
          setPatientFetchError(
            e?.response?.data?.error || 'Não foi possível carregar o paciente para a evolução.',
          );
        }
      } finally {
        if (!cancelled) setPatientLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [join?.appointmentId, join?.patient?.id]);

  const handleLeft = useCallback(() => {
    if (join?.sessionId) {
      leaveSessionAuth(join.sessionId);
    }
  }, [join]);

  const handleConferenceJoined = useCallback(() => {
    setCallStartedAt((prev) => prev ?? Date.now());
  }, []);

  const patientForEvolution = join?.patient ?? resolvedPatient ?? null;

  if (join) {
    const appointmentLabel = `${join.appointment.date.split('-').reverse().join('/')} às ${join.appointment.startTime.slice(0, 5)}`;
    return (
      <Box sx={teleconsultPageColumnSx}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            px: { xs: 1.5, sm: 2 },
            py: 1.25,
            mb: { xs: 1, md: 1.5 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexShrink: 0,
          }}
        >
          <TeleconsultCallTimer startedAt={callStartedAt} />
          {patientForEvolution && (
            <Chip
              size="small"
              label={patientForEvolution.full_name}
              sx={{ bgcolor: '#f0fdfa', color: '#0f766e', fontWeight: 600, maxWidth: { xs: 140, sm: 220 } }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant={agendaDrawerOpen ? 'contained' : 'outlined'}
            startIcon={<CalendarMonthIcon />}
            onClick={() => setAgendaDrawerOpen(true)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              ...(agendaDrawerOpen
                ? { bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' } }
                : { borderColor: '#14b8a6', color: '#0f766e' }),
            }}
          >
            Agenda
          </Button>
          {!evolutionOpen && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AssignmentIcon />}
              onClick={() => setEvolutionOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#cbd5e1', color: '#475569' }}
            >
              Evolução
            </Button>
          )}
        </Paper>

        <Box sx={teleconsultPageSx}>
        <Paper
          elevation={0}
          sx={{
            ...teleconsultPanelCardSx,
            position: 'relative',
            flex: isNarrow ? '0 0 auto' : { md: '1 1 58%' },
            minHeight: isNarrow ? 'min(42vh, 520px)' : 0,
            height: isNarrow ? 'min(42vh, 520px)' : 'auto',
          }}
        >
          <TeleconsultaRoom
            layout="embedded"
            participantRole="professional"
            join={join}
            title="Teleconsulta"
            subtitle={
              config.teleconsult.browserRecording
                ? `Consulta de ${appointmentLabel} · gravação no browser (áudio da aba) para transcrição`
                : `Consulta de ${appointmentLabel}`
            }
            useBrowserRecording={config.teleconsult.browserRecording}
            autoStartBrowserRecording={config.teleconsult.autoStartBrowserRecording}
            autoStartFileRecording={config.teleconsult.autoStartRecording}
            afterLeftMessage="A consulta foi encerrada. A gravação segue em processamento; use o painel ao lado para a evolução clínica."
            onLeft={handleLeft}
            onConferenceJoined={handleConferenceJoined}
          />
          {!evolutionOpen && (
            <Tooltip title="Abrir nova evolução clínica">
              <Fab
                size="medium"
                aria-label="Abrir nova evolução clínica"
                onClick={() => setEvolutionOpen(true)}
                sx={{
                  position: 'absolute',
                  bottom: 20,
                  right: 20,
                  zIndex: 6,
                  background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
                  color: '#fff',
                  '&:hover': { background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)' },
                }}
              >
                <AssignmentIcon />
              </Fab>
            </Tooltip>
          )}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            ...teleconsultPanelCardSx,
            flex: isNarrow ? '1 1 auto' : { md: '1 1 42%' },
            minHeight: isNarrow ? 380 : 0,
            display: evolutionOpen ? 'flex' : 'none',
          }}
        >
          {patientLoading && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
              <CircularProgress sx={{ color: '#14B8A6' }} />
            </Box>
          )}
          {!patientLoading && patientFetchError && !patientForEvolution && (
            <Alert severity="warning" sx={{ m: 2 }}>
              {patientFetchError}
            </Alert>
          )}
          {!patientLoading && patientForEvolution && (
            <HealthcareEvolutionDialog
              open={evolutionOpen}
              embedded
              patient={patientForEvolution}
              onClose={() => setEvolutionOpen(false)}
              onError={(msg) => {
                console.error(msg);
              }}
            />
          )}
        </Paper>
        </Box>

        <TeleconsultAgendaDrawer
          open={agendaDrawerOpen}
          onClose={() => setAgendaDrawerOpen(false)}
          patientId={patientForEvolution?.id}
          patientName={patientForEvolution?.full_name}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Stack spacing={3} sx={{ maxWidth: 480, width: '100%' }} alignItems="center">
        {loading && (
          <>
            <CircularProgress sx={{ color: '#14B8A6' }} />
            <Typography color="text.secondary">Preparando a sala…</Typography>
          </>
        )}
        {!loading && error && (
          <>
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={() => navigate('/agenda')}>
                Voltar à agenda
              </Button>
              <Button
                variant="contained"
                startIcon={<VideoCallIcon />}
                onClick={init}
                sx={{
                  background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
                }}
              >
                Tentar novamente
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default ProfessionalTeleconsultPage;
